import React, { forwardRef, useEffect } from "react";
import { cn } from "app/lib/utils";
import SendButton from "./SendButton";

interface Props {
  readonly message?: string;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly isLoginless?: boolean;
  readonly setMessage?: (message: string) => void;
  readonly handleSendMessage?: (message: string) => void;
}

const ChatInput = forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      message = "",
      placeholder = "Type your response...",
      disabled = false,
      isLoginless = false,
      setMessage,
      handleSendMessage,
    },
    ref
  ) => {
    useEffect(() => {
      // Auto-resize textarea for enabled state
      if (!disabled && ref && "current" in ref && ref.current) {
        const textarea = ref.current;

        // Start at a true single-line height when empty
        if (!message) {
          textarea.style.height = "24px";
          return;
        }

        // For non-empty content, grow to fit text
        textarea.style.height = "auto";
        const newHeight = Math.max(24, textarea.scrollHeight);
        textarea.style.height = `${newHeight}px`;
      }
    }, [message, ref, disabled]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (disabled || !handleSendMessage) return;

      if (event.key !== "Enter") return;
      event.preventDefault();

      // For loginless mode, always send on Enter (no multi-line support)
      if (isLoginless) {
        handleSendMessage(message);
        return;
      }

      // For regular mode, allow Shift+Enter for new lines
      if (event.shiftKey) {
        const target = event.target as HTMLTextAreaElement;
        const { selectionStart, selectionEnd } = target;
        const newText =
          message.substring(0, selectionStart) +
          "\n" +
          message.substring(selectionEnd);
        setMessage?.(newText);
        return;
      }
      handleSendMessage(message);
    };

    const handleSendClick = () => {
      if (disabled || !handleSendMessage || !message.trim()) return;
      handleSendMessage(message);
    };

    if (disabled) {
      // Disabled state - matches DisabledChatInput styling
      return (
        <div className="flex items-center bg-white rounded-md px-3 py-2 gap-2 border border-[#ddddde] h-10">
          <p className="flex-1 text-[13px] leading-6 text-[#ddddde] m-0">
            {placeholder}
          </p>
        </div>
      );
    }

    // Enabled state using shadcn Textarea and Button
    return (
      <div
        className={cn(
          "flex items-end bg-white rounded-md px-3 py-2 gap-2 border border-[#a9aaac] transition-all",
          "focus-within:border-blue-500 overflow-hidden"
        )}
      >
        <textarea
          placeholder={placeholder}
          value={message}
          onChange={(event) => setMessage?.(event.target.value)}
          onKeyDown={handleKeyDown}
          ref={ref}
          rows={1}
          style={{
            wordWrap: "break-word",
            overflowWrap: "break-word",
            width: "0",
            minWidth: "0",
          }}
          className={cn(
            "flex-1 min-h-[24px] max-h-[200px] border-0 p-0 text-[13px] leading-6 resize-none bg-transparent",
            "outline-none overflow-hidden min-w-0 whitespace-pre-wrap break-words",
            "placeholder:text-muted-foreground"
          )}
        />
        {message.trim() && (
          <SendButton onClick={handleSendClick} hasMessage={true} />
        )}
      </div>
    );
  }
);

ChatInput.displayName = "ChatInput";

export default ChatInput;
